import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container, Typography, TextField, Button, Paper, Stack, Box, Snackbar, Alert,
  IconButton, Grid, Avatar, Tooltip, MenuItem, useMediaQuery, Divider, Pagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon,
  Logout as LogoutIcon, GridOn as GridOnIcon, Add as AddIcon,
  Engineering as TechIcon
} from "@mui/icons-material";
import CloseIcon from '@mui/icons-material/Close';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const USER_URL = `${process.env.REACT_APP_API_URL}/api/tele`;

// ===== WHITE THEME WITH YELLOW ACCENTS =====
const lightStyles = {
  container: { minHeight: "100vh", bgcolor: "#f5f5f5", py: 4 },
  paper: {
    bgcolor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
  },
  actionBtn: {
    bgcolor: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": { 
      bgcolor: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  },
  dialogPaper: {
    bgcolor: "#ffffff",
    border: "2px solid #FFD600",
    color: "#000000",
    borderRadius: "16px",
  },
  tableHeader: {
    bgcolor: "#f5f5f5",
    color: "#000000",
    fontWeight: "bold",
    borderBottom: "2px solid #FFD600",
  },
  tableCell: {
    color: "#000000",
    borderBottom: "1px solid #e0e0e0",
  },
  cancelBtn: {
    color: "#666666",
    fontWeight: "bold",
    fontSize: "0.75rem",
    letterSpacing: "1px",
    px: 2.5,
    py: 0.5,
    borderRadius: "20px",
    border: "1px solid #e0e0e0",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      borderColor: "#ff4444",
      color: "#ff4444",
      bgcolor: "rgba(255, 68, 68, 0.05)",
    },
  },
};

const TechnicianManagement = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const token = localStorage.getItem("token");

  // State
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "", username: "", password: "", primaryContact: "", secondaryContact: ""
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (msg, severity = "success") => setSnackbar({ open: true, message: msg, severity });

  // Fetch Technicians
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${USER_URL}/users/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showSnackbar("Failed to fetch technicians", "error");
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Handlers
  const handleOpenCreate = () => {
    setEditId(null);
    setNewUser({ name: "", username: "", password: "", primaryContact: "", secondaryContact: "" });
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditId(user._id);
    setNewUser({ ...user, password: "" });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this technician?")) return;
    try {
      await axios.delete(`${USER_URL}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      showSnackbar("Technician deleted", "warning");
      fetchUsers();
    } catch (err) {
      showSnackbar("Delete failed", "error");
    }
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await axios.put(`${USER_URL}/${editId}`, newUser, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Technician updated successfully");
      } else {
        await axios.post(`${USER_URL}/create`, newUser, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Technician created successfully");
      }
      setIsDialogOpen(false);
      fetchUsers();
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Operation failed", "error");
    }
  };

  const downloadExcel = () => {
    const data = filteredUsers.map(u => ({ Name: u.name, User: u.username, Contact: u.primaryContact }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Technicians");
    XLSX.writeFile(wb, "Technician_List.xlsx");
    showSnackbar("Excel exported", "success");
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u =>
      u?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u?.username?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  return (
    <Box sx={lightStyles.container}>
      <Container maxWidth="lg">

        {/* HEADER */}
        <Paper sx={{ p: 2, mb: 4, ...lightStyles.paper, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export Excel">
              <IconButton 
                onClick={downloadExcel} 
                sx={{ 
                  color: '#FFD600', 
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    color: '#ffcc00',
                    borderColor: '#FFD600',
                    backgroundColor: 'rgba(255, 214, 0, 0.1)',
                  }
                }}
              >
                <GridOnIcon />
              </IconButton>
            </Tooltip>
            {isMobile ? (
              <Tooltip title="Logout">
                <IconButton
                  onClick={() => { localStorage.removeItem("token"); navigate("/login"); }}
                  sx={{ color: "#ff4444", border: "1px solid #e0e0e0", bgcolor: "rgba(255, 0, 0, 0.05)" }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                onClick={() => { localStorage.removeItem("token"); navigate("/login"); }}
                startIcon={<LogoutIcon sx={{ color: "#ff0000" }} />}
                sx={{ borderColor: "#e0e0e0", color: "#ff4444", "&:hover": { bgcolor: "rgba(255, 0, 0, 0.1)" } }}
              >
                Logout
              </Button>
            )}
          </Stack>
        </Paper>

        {/* ACTION TOOLBAR */}
        <Paper sx={{ p: 1.5, mb: 3, ...lightStyles.paper, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "nowrap" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexGrow: isMobile ? 1 : 0, minWidth: 0 }}>
            {isMobile ? (
              <IconButton onClick={handleOpenCreate} sx={{ ...lightStyles.actionBtn, borderRadius: 1, width: 40, height: 40 }}>
                <AddIcon />
              </IconButton>
            ) : (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ ...lightStyles.actionBtn, whiteSpace: 'nowrap' }}>
                New Technician
              </Button>
            )}
            <TextField
              placeholder={isMobile ? "Search..." : "Search technicians..."}
              size="small"
              sx={{ ...lightStyles.input, width: isMobile ? 'auto' : '300px', flexGrow: isMobile ? 1 : 0 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
            <Tooltip title="Refresh Database">
              <IconButton
                onClick={fetchUsers}
                sx={{
                  color: '#FFD600',
                  border: '1px solid #e0e0e0',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    color: '#ffcc00',
                    borderColor: '#FFD600',
                    backgroundColor: 'rgba(255, 214, 0, 0.1)',
                    boxShadow: '0 0 12px rgba(255, 214, 0, 0.4)'
                  }
                }}
              >
                <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* DATA TABLE */}
        <TableContainer component={Paper} sx={{ ...lightStyles.paper, maxHeight: '65vh', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['NAME', 'USERNAME', 'PRIMARY CONTACT', 'ACTIONS'].map(h => (
                  <TableCell key={h} sx={lightStyles.tableHeader}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u._id} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 214, 0, 0.05)' } }}>
                  <TableCell sx={{ color: '#000000', fontWeight: 'bold' }}>{u.name}</TableCell>
                  <TableCell sx={{ color: '#000000' }}>{u.username}</TableCell>
                  <TableCell sx={{ color: '#666666' }}>{u.primaryContact || "-"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small" onClick={() => handleOpenEdit(u)} sx={{ color: "#FFD600", "&:hover": { bgcolor: "#FFD600", color: "#000" } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(u._id)} sx={{ color: "#ff4444", "&:hover": { bgcolor: "#ff0000", color: "#fff" } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', color: '#666666', py: 4 }}>
                    No technicians found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* WHITE DIALOG WITH YELLOW ACCENTS */}
        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          PaperProps={{ sx: lightStyles.dialogPaper }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle
            sx={{
              color: '#000000',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              pt: 3,
              pb: 1,
              letterSpacing: '1.5px',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            {editId ? "UPDATE PROTOCOL" : "SYSTEM REGISTRY"}
          </DialogTitle>

          <DialogContent sx={{ px: isMobile ? 2 : 4, pb: 2, mt: 2 }}>
            <Stack spacing={2}>
              {[
                { label: "FULL NAME", name: "name", type: "text" },
                { label: "USERNAME", name: "username", type: "text" },
                { label: "SECURITY KEY", name: "password", type: "password" },
                { label: "PRIMARY CONTACT", name: "primaryContact", type: "text" },
                { label: "SECONDARY CONTACT", name: "secondaryContact", type: "text" },
              ].map((field) => (
                <TextField
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  type={field.type}
                  fullWidth
                  size="small"
                  value={newUser[field.name]}
                  onChange={(e) => setNewUser({ ...newUser, [field.name]: e.target.value })}
                  sx={lightStyles.input}
                />
              ))}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 4, pb: 4, pt: 1, justifyContent: 'space-between', borderTop: '1px solid #e0e0e0' }}>
            <Button
              onClick={() => setIsDialogOpen(false)}
              startIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
              sx={lightStyles.cancelBtn}
            >
              ABORT
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={lightStyles.actionBtn}
            >
              COMMIT
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert 
            severity={snackbar.severity} 
            variant="filled" 
            sx={{ bgcolor: '#ffffff', color: '#000000', border: `1px solid ${snackbar.severity === 'success' ? '#FFD600' : '#ff4444'}` }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Container>
    </Box>
  );
};

export default TechnicianManagement;